import {
  Breadcrumbs,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import z from "zod";
import { HandleReply } from "@/components/actions/handle-reply";
import { MonthwiseActivityTable } from "@/components/activity/months-activity";
import { ButtonOrTooltip } from "@/components/core/conditional-tooltip";
import { onError } from "@/components/core/display-error";
import { ErrorDisplay } from "@/components/core/error-display";
import { CardLoadingSkeleton } from "@/components/core/loading-skeleton";
import { Link, LinkButton } from "@/components/core/tanstack-link";
import { CustomerAddNote } from "@/components/customer/add-note";
import { EmailDetailsForm, LetterDetailsForm, SMSDetailsForm } from "@/components/events/details";
import { EmailEditForm, SMSEditForm } from "@/components/events/edit";
import { EmailSummary } from "@/components/events/summary";
import { useTrovePlan } from "@/hooks/trove-plan";
import {
  EmailDetailsOut,
  EventKind,
  EventStatus,
  EventStatusOut,
  getEventDetails,
  getGetEventDetailsQueryKey,
  getGetEventsQueryKey,
  getGetToNotifyQueryKey,
  InboundCallEventDetailsOut,
  InboundEmailEventDetailsOut,
  InboundSmsEventDetailsOut,
  NoteStatus,
  NoteTroveType,
  OutboundEventDetailsOut,
  OutboundSmsEventDetailsOut,
  PersonalReminderEventDetailsOut,
  PhoneReminderEventDetailsOut,
  PricingStatusStatus,
  SmsDetailOut,
  useExecuteEvent,
  useGetActionsByEvent,
  useGetEventDetails,
  useSendEmailToMe,
  OutboundLetterEventDetailsOut,
} from "@/models/backend";
import { CombinedEventStatus, EventDetailsOut } from "@/components/activity/event-status";
import { isAxiosError } from "axios";
import { NoteDisplay } from "@/components/activity/details-panel";
import { queryClient } from "@/helpers/tanstack-client";

const searchParams = z.object({
  page: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/_contained/events/$eventId")({
  component: RouteComponent,
  validateSearch: searchParams,
  loader: async ({ params: { eventId } }) => {
    try {
      await queryClient.query({
        queryFn: (signal) => getEventDetails(eventId, signal),
        queryKey: getGetEventDetailsQueryKey(eventId),
        staleTime: "static",
      });
    } catch (e) {
      if (isAxiosError(e) && e.status == 404) {
        throw notFound();
      }
    }
  },
});

const EmailEventDetails = ({
  event,
  setEditMode,
  editMode,
}: {
  event:
    | InboundEmailEventDetailsOut
    | OutboundEventDetailsOut
    | OutboundLetterEventDetailsOut
    | PhoneReminderEventDetailsOut
    | PersonalReminderEventDetailsOut;
  setEditMode: (value: boolean) => void;
  editMode: boolean;
}) => {
  const { data: actions, isLoading } = useGetActionsByEvent(event.id);
  if (isLoading) {
    return <CardLoadingSkeleton />;
  }
  if (
    (event.kind == EventKind.inbound_email || event.kind == EventKind.outbound_email) &&
    actions &&
    actions.length > 0
  ) {
    return <HandleReply event={event} actions={actions} />;
  }
  if (event.kind == EventKind.outbound_letter) {
    return <LetterDetailsForm event={event as OutboundLetterEventDetailsOut} />;
  }
  if (
    actions == null &&
    (
      [
        EventStatusOut.pending,
        EventStatusOut.pending_overdue,
        EventStatusOut.pending_approved,
        EventStatusOut.queued,
      ] as CombinedEventStatus[]
    ).includes(event.status)
  ) {
    return editMode ? (
      <EmailEditForm
        event={event as EventDetailsOut & { email: EmailDetailsOut }}
        endEditMode={() => setEditMode(false)}
      />
    ) : (
      <EmailDetailsForm
        event={event as EventDetailsOut & { email: EmailDetailsOut }}
        startEditMode={() => setEditMode(true)}
      />
    );
  }
  // Some kind of alert probs
  return (
    <>
      {event.status === EventStatus.superseded ? (
        <Typography>This email was replaced by a newer one and won't send</Typography>
      ) : null}
      {event.status === EventStatus.failure ? (
        <Typography>This email failed to send</Typography>
      ) : null}
      <EmailSummary event={event as EventDetailsOut & { email: EmailDetailsOut }} />
    </>
  );
};

const SmsEventDetails = ({
  event,
  setEditMode,
  editMode,
}: {
  event: InboundSmsEventDetailsOut | OutboundSmsEventDetailsOut | InboundCallEventDetailsOut;
  setEditMode: (value: boolean) => void;
  editMode: boolean;
}) => {
  const { data: actions, isLoading } = useGetActionsByEvent(event.id);
  if (isLoading) {
    return <CardLoadingSkeleton />;
  }
  if (actions && actions.length > 0) {
    return <HandleReply event={event} actions={actions} />;
  }
  if (
    (
      [
        EventStatusOut.pending,
        EventStatusOut.pending_overdue,
        EventStatusOut.pending_approved,
        EventStatusOut.queued,
      ] as CombinedEventStatus[]
    ).includes(event.status)
  ) {
    return editMode ? (
      <SMSEditForm
        event={event as EventDetailsOut & { sms: SmsDetailOut }}
        endEditMode={() => setEditMode(false)}
      />
    ) : (
      <SMSDetailsForm
        event={event as EventDetailsOut & { sms: SmsDetailOut }}
        startEditMode={() => setEditMode(true)}
      />
    );
  }
};

const EventDetails = ({
  setEditMode,
  editMode,
  event,
}: {
  setEditMode: (value: boolean) => void;
  editMode: boolean;
  event: EventDetailsOut;
}) => {
  if (
    event.kind == EventKind.note ||
    (event.kind == EventKind.phonecall_reminder &&
      ([NoteStatus.published, NoteStatus.unpublished] as CombinedEventStatus[]).includes(
        event.status,
      ))
  ) {
    return <NoteDisplay isLoading={false} note={event.note[0]} />;
  } else if (
    event.kind === EventKind.inbound_email ||
    event.kind === EventKind.outbound_email ||
    event.kind === EventKind.outbound_letter ||
    event.kind === EventKind.phonecall_reminder ||
    event.kind === EventKind.personal_reminder
  ) {
    return <EmailEventDetails event={event} editMode={editMode} setEditMode={setEditMode} />;
  } else {
    return <SmsEventDetails event={event} editMode={editMode} setEditMode={setEditMode} />;
  }
};

function RouteComponent() {
  const { eventId } = Route.useParams();
  const {
    data: event,
    isError: isErrorEvent,
    error: eventError,
    refetch: refetchEvent,
  } = useGetEventDetails(eventId);
  const [editMode, setEditMode] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const customer = event?.customer ?? null;
  const { status } = useTrovePlan();

  const { mutate: sendToMe, isPending: isCopyPending } = useSendEmailToMe({
    mutation: {
      onSuccess: () => {
        enqueueSnackbar("Sent a copy to your email - if you can't find it, check your outbox", {
          variant: "success",
        });
      },
      onError: (_error) => {
        enqueueSnackbar("Couldn't send email", { variant: "error" });
      },
    },
  });

  const queryClient = useQueryClient();
  const theme = useTheme();

  const { mutate: sendEmail, isPending } = useExecuteEvent();

  if (isErrorEvent) {
    return (
      <ErrorDisplay
        title="Failed to load email"
        error={eventError}
        onRetry={() => refetchEvent()}
      />
    );
  }

  if (!event) return null;

  const onSendCopyNow = () => {
    if (!("email" in event)) return;
    sendToMe({ emailId: event.email.id });
  };

  const onSendNow = (markOnly: boolean) => {
    sendEmail(
      { data: [event.id], params: { only_mark_as_sent: markOnly } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getGetEventDetailsQueryKey(event?.id ?? ""),
          });
          await queryClient.invalidateQueries({
            queryKey: getGetEventsQueryKey().slice(0, 2),
          });
          await queryClient.invalidateQueries({
            queryKey: getGetToNotifyQueryKey(),
          });
          if (markOnly) {
            enqueueSnackbar(
              event.kind == EventKind.outbound_sms
                ? "SMS skipped and won't be sent"
                : "Email skipped and won't be sent",
              {
                variant: "success",
                style: {
                  borderWidth: "1px",
                  borderColor: theme.palette.success.main,
                  background: theme.palette.background.paper,
                  color: theme.palette.success.dark,
                },
              },
            );
          } else {
            enqueueSnackbar(
              event.kind == EventKind.outbound_sms
                ? "SMS was sent"
                : event.kind == EventKind.outbound_letter
                  ? "Letter was queued for printing"
                  : "Email was sent",
              { variant: "success" },
            );
          }
        },
        onError: onError({
          prefix: markOnly ? "Couldn't skip sending email" : "Couldn't send email",
        }),
      },
    );
  };

  return (
    <Stack spacing={4}>
      <Breadcrumbs separator=">" aria-label="breadcrumb">
        <Link to="/events">Activity</Link>
        {customer != null && <Typography>{customer.name}</Typography>}
      </Breadcrumbs>
      <Stack spacing={1} sx={{ alignItems: "start" }}>
        <Stack direction="row" sx={{ width: "100%" }}>
          <Typography variant="h3">{customer?.name ?? "Unknown customer"}</Typography>
          <Stack spacing={1} sx={{ ml: "auto", textWrapMode: "nowrap", flexWrap: "wrap" }}>
            <Tooltip title={editMode ? "Save changes to email before sending" : null}>
              <Stack spacing={2} direction="row" sx={{ marginLeft: "auto", marginBottom: 2 }}>
                {event.kind == EventKind.note ||
                (event.kind == EventKind.phonecall_reminder &&
                  (
                    [NoteStatus.published, NoteStatus.unpublished] as CombinedEventStatus[]
                  ).includes(event.status)) ? (
                  <Button
                    variant="contained"
                    onClick={() => setShowAddNote(true)}
                    disabled={
                      !(Object.values(NoteTroveType) as string[]).includes(event.type ?? "")
                    }
                  >
                    Edit note
                  </Button>
                ) : (
                  (event.kind == EventKind.outbound_email ||
                    event.kind == EventKind.outbound_sms) && (
                    <>
                      <ButtonOrTooltip
                        title="You don't have an active subscription. Go to settings to set it up."
                        variant="outlined"
                        onClick={() => onSendNow(true)}
                        color="warning"
                        startIcon={isCopyPending ? <CircularProgress size={20} /> : null}
                        tooltipDisabled={status === PricingStatusStatus.ERROR}
                        disabled={
                          editMode ||
                          isCopyPending ||
                          !(
                            [
                              EventStatusOut.pending,
                              EventStatusOut.pending_overdue,
                              EventStatusOut.pending_approved,
                            ] as CombinedEventStatus[]
                          ).includes(event.status)
                        }
                        size="small"
                      >
                        Skip sending
                      </ButtonOrTooltip>
                      <ButtonOrTooltip
                        title="You don't have an active subscription. Go to settings to set it up."
                        variant="outlined"
                        onClick={onSendCopyNow}
                        color="secondary"
                        startIcon={isCopyPending ? <CircularProgress size={20} /> : null}
                        tooltipDisabled={status === PricingStatusStatus.ERROR}
                        disabled={
                          editMode ||
                          isCopyPending ||
                          !(
                            [
                              EventStatusOut.pending,
                              EventStatusOut.pending_overdue,
                              EventStatusOut.pending_approved,
                            ] as CombinedEventStatus[]
                          ).includes(event.status)
                        }
                        size="small"
                      >
                        Send copy to myself
                      </ButtonOrTooltip>
                      <ButtonOrTooltip
                        title="You don't have an active subscription. Go to settings to set it up."
                        variant="contained"
                        onClick={() => onSendNow(false)}
                        startIcon={isPending ? <CircularProgress size={20} /> : null}
                        tooltipDisabled={status === PricingStatusStatus.ERROR}
                        disabled={
                          editMode ||
                          isPending ||
                          !(
                            [
                              EventStatusOut.pending,
                              EventStatusOut.pending_overdue,
                              EventStatusOut.pending_approved,
                            ] as CombinedEventStatus[]
                          ).includes(event.status)
                        }
                        size="small"
                      >
                        Send now
                      </ButtonOrTooltip>
                    </>
                  )
                )}
              </Stack>
            </Tooltip>
          </Stack>
        </Stack>
        {customer != null && (
          <LinkButton
            to="/customers/$customerId"
            params={{ customerId: customer.id }}
            variant="text"
            sx={{ px: 0, py: 0.5 }}
          >
            Go to Customer
          </LinkButton>
        )}
      </Stack>
      <EventDetails setEditMode={setEditMode} editMode={editMode} event={event} />
      {customer != null && (
        <>
          <Stack spacing={2}>
            <Typography variant="h6">All customer activity</Typography>
            <MonthwiseActivityTable customerId={customer.id} />
          </Stack>
          <CustomerAddNote
            customer={customer}
            open={showAddNote}
            onClose={() => setShowAddNote(false)}
            selectedInvoices={event.invoices}
            publishTicked={false}
            eventId={event.id}
          />
        </>
      )}
    </Stack>
  );
}
